import { displayMessage } from '../alert/messageFunc.js';
import { charts } from '../charts/charts.js';
import { publishPredictionChartTeamSelection } from '../charts/predictionChartSelection.js';
import { translate } from '../helpers/translations.js';
import { deriveQuotes, isCompleteOrderBookPayload } from '../helpers/marketQuotes.js';
import { applyQuotes } from './prediction/quotesSync.js';
import { consumeOutcomeHash, pickDefaultBetItem } from './prediction/outcomeHash.js';

class PredictionsPage {
    // Constants for magic numbers
    static ORDER_BOOK_REFRESH_INTERVAL = 5000; // 5 seconds
    static ORDER_BOOK_SCROLL_DELAY = 50; // milliseconds
    static ORDER_BOOK_HEIGHT = '360px';
    static ORDER_BOOK_MIN_HEIGHT = '120px';
    static VALIDATION_ERROR_STYLE = 'display: block; width: 100%; box-sizing: border-box; color: #ff4444; font-size: 12px; line-height: 1.3; margin-top: 5px; text-align: center; padding: 5px; background: rgba(255, 68, 68, 0.1); border-radius: 4px;';
    static VALIDATION_INPUT_SHADOW = 'inset 0 0 0 1px #ff4444';
    static MIN_SHARE_PRICE = 0.01;
    static MAX_SHARE_PRICE = 0.99;

    constructor() {
        // Prevent multiple initialization
        if (window.predictionsPageInstance) {
            window.predictionsPageInstance.destroy();
        }

        this.currentSelectedTeam = null;
        this.currentMarketEventTeamId = null;
        this.orderBookData = {};
        this.allOrderBookData = {};
        this.lastTradeData = {};
        this.userOrdersData = [];
        this.userActiveBetsData = [];
        this.marketEventId = this.getMarketEventId();
        this.orderBookRefreshInterval = null;
        this.chartOwner = null;
        this.chartModulePromise = null;
        this.expectedTeamIds = this.collectExpectedTeamIds();
        this.boundEventHandlers = {};
        this.isDestroyed = false;

        // Store global reference for cleanup
        window.predictionsPageInstance = this;

        this.initializeEventListeners();
        this.loadUserOrdersData();
        this.loadUserActiveBetsData();
        this.initializeOrderBook();
        this.initializeChart();
        this.initializeDefaultSelection();
        this.setupPageVisibilityHandler();
        this.initializeOrderTypeState();

    }

    destroy() {
        if (this.isDestroyed) {
            return;
        }
        
        this.isDestroyed = true;
        
        // Clear intervals
        this.stopOrderBookRefresh();
        
        // Remove event listeners
        this.removeEventListeners();
        
        // Уничтожение графика. Ждать промис нельзя — destroy() синхронный,
        // поэтому вешаем колбэк: если модуль ещё грузится, снос произойдёт
        // сразу после загрузки. Токен захватывается локально, потому что
        // ниже поля инстанса обнуляются.
        const chartOwner = this.chartOwner;
        if (this.chartModulePromise) {
            this.chartModulePromise
                .then(module => module && module.destroyMarketEventChart(chartOwner))
                .catch(() => {});
            this.chartModulePromise = null;
        }
        
        // Clear references
        this.orderBookData = null;
        this.allOrderBookData = null;
        this.lastTradeData = null;
        this.userOrdersData = null;
        this.userActiveBetsData = null;
        this.boundEventHandlers = null;
        
        // Remove global reference
        if (window.predictionsPageInstance === this) {
            window.predictionsPageInstance = null;
        }
        
    }

    removeEventListeners() {
        // Remove document event listeners if they were bound
        Object.keys(this.boundEventHandlers).forEach(key => {
            const handler = this.boundEventHandlers[key];
            if (handler && handler.element && handler.event && handler.callback) {
                handler.element.removeEventListener(handler.event, handler.callback, handler.options);
            }
        });
        
        // Clear bound handlers
        this.boundEventHandlers = {};
    }

    setupPageVisibilityHandler() {
        // Add visibilitychange event as backup cleanup mechanism
        const visibilityHandler = () => {
            if (document.hidden) {
                this.stopOrderBookRefresh();
            } else if (!this.isDestroyed && this.marketEventId) {
                this.startOrderBookRefresh();
            }
        };
        
        // Store handler for cleanup
        this.boundEventHandlers.visibilityChange = {
            element: document,
            event: 'visibilitychange',
            callback: visibilityHandler,
            options: false
        };
        
        document.addEventListener('visibilitychange', visibilityHandler);
    }

    initializeOrderTypeState() {
        // Check initial state of market order checkboxes and hide price inputs if needed
        const forms = document.querySelectorAll('.market-bet--buy-menu');

        forms.forEach(form => {
            const checkbox = form.querySelector('.js_cof_change_agree');
            const priceInput = form.querySelector('.js_price_per_share');

            if (!checkbox || !priceInput) return;

            const priceWrapper = priceInput.closest('.iconed-input');
            if (!priceWrapper) return;

            // If checkbox is checked (market order), hide price input immediately
            if (checkbox.checked) {
                priceWrapper.style.display = 'none';
                priceWrapper.classList.add('disabled');
                priceInput.disabled = true;
            } else {
                // Ensure price input is visible for limit orders
                priceWrapper.style.display = '';
                priceWrapper.classList.remove('disabled');
                priceInput.disabled = false;
            }
        });
    }

    initializeDefaultSelection() {
        
        // По умолчанию yes, а #yes или #no в ссылке (баннер btc15m) выбирает исход сразу
        const hashOutcome = consumeOutcomeHash(window.location, window.history);
        const defaultBetItem = pickDefaultBetItem(document.querySelectorAll('.bet-item[data-team]'), hashOutcome);
        
        if (defaultBetItem) {
            
            // Get team data
            const team = defaultBetItem.getAttribute('data-team');
            const teamTitle = defaultBetItem.getAttribute('data-team_title');
            const teamLogo = defaultBetItem.getAttribute('data-team_logo');
            const percent = defaultBetItem.getAttribute('data-percent');
            const marketEventTeamId = defaultBetItem.getAttribute('data-market_event_team_id');
            
            // Set current selection
            this.currentSelectedTeam = team;
            this.currentMarketEventTeamId = marketEventTeamId;
            
            // Apply selection to UI
            this.syncAllRelatedElements({ 
                team, 
                teamTitle, 
                teamLogo, 
                percent, 
                marketEventTeamId 
            });
            
            // Initialize sell form with position data for default selection
            const sellForm = document.querySelector('.market-bet--buy-menu[data-tab="#sell"]');
            if (sellForm) {
                this.updateSellFormPositionData(sellForm, team);
            }
            
        } else {
        }
    }

    initializeEventListeners() {
        // Custom event listeners for predictions-specific functionality
        // Note: Basic events (bet-item clicks, tabs, alerts, toggles, range inputs, form validation) 
        // are already handled by eventDocument.js, so we only add prediction-specific handlers

        // Order book price level clicks - not handled by eventDocument.js
        document.addEventListener('click', (e) => {
            const orderBookLine = e.target.closest('.market-bet--order-book--line:not(.market-bet--order-book--line-head):not(.market-bet--order-book--line-central)');
            if (orderBookLine) {
                this.handleOrderBookClick(orderBookLine);
            }
        });


        // Form submission handlers - not handled by eventDocument.js
        document.addEventListener('submit', (e) => {
            const form = e.target.closest('.form_for_js_in_div');
            if (form && form.closest('.market-bet--buy-menu')) {
                // Form submission will use default browser behavior
            }
        });

        // Predictions-specific input handlers - extend the existing input handling
        document.addEventListener('input', (e) => {
            const target = e.target;
            
            // Additional validation and calculations for predictions page
            if (target.classList.contains('input-range-value') && target.closest('.market-bet--buy-menu')) {
                // Clear previous validation errors for this input before new validation
                this.showValidationError(target, '', true);
                this.validateBetAmount(target);
                this.updateBetCalculations(target);
            }

            // Range slider inputs - handle bet calculations when range sliders change
            if (target.classList.contains('input-range') && target.closest('.market-bet--buy-menu')) {
                // Find the corresponding input-range-value element to trigger calculations
                const parent = target.closest('.bet__range');
                const inputRangeValue = parent?.querySelector('.input-range-value');
                if (inputRangeValue) {
                    // Clear previous validation errors for this input before new validation
                    this.showValidationError(inputRangeValue, '', true);
                    this.validateBetAmount(inputRangeValue);
                    this.updateBetCalculations(inputRangeValue);
                }
            }

            // Price per share inputs - predictions specific
            if (target.classList.contains('js_price_per_share')) {
                // Юзер тронул цену руками — тик больше её не перезаписывает.
                target.setAttribute('data-user-edited', '1');
                this.updatePricePerShare(target);
                this.validatePrice(target, { allowEmpty: true });
            }
        });

        // Focus handlers for input-range-value - clear zero values for better UX
        document.addEventListener('focusin', (e) => {
            const target = e.target;
            
            // Clear zero values when focusing on range value inputs
            if (target.classList.contains('input-range-value') && target.closest('.market-bet--buy-menu')) {
                if (target.value === '0') {
                    target.value = '';
                }
            }
        });

        // Checkbox handlers for market/limit orders - predictions specific
        document.addEventListener('change', (e) => {
            if (e.target.classList.contains('js_cof_change_agree') && e.target.closest('.market-bet--buy-menu')) {
                this.handleOrderTypeChange(e.target);
            }

            // Yes/No radio button handlers - predictions specific
            if ((e.target.name === 'team_buy' || e.target.name === 'team_sell') && e.target.closest('.market-bet--buy-menu')) {
                this.handleTeamSelection(e.target);
            }
        });

        // Override bet item click handling for predictions-specific behavior
        // We'll listen for the event after eventDocument.js processes it
        document.addEventListener('click', (e) => {
            const betItem = e.target.closest('.bet-item');
            if (betItem && betItem.getAttribute('data-team') && betItem.getAttribute('data-market_event_team_id')) {
                
                // Use a single timeout to ensure we run after eventDocument.js
                setTimeout(() => {
                    this.handleBetItemClick(betItem);
                }, 0);
            }
            
            // Handle tab clicks to update orderbook for direction changes
            const tabElement = e.target.closest('.tabs__item.js-tab');
            if (tabElement) {
                // Use a timeout to ensure the tab is activated first
                setTimeout(() => {
                    // Re-render the entire orderbook based on new direction
                    this.renderOrderBook();
                    
                    // Clear validation errors when switching between buy/sell tabs
                    const tabData = tabElement.getAttribute('href') || tabElement.dataset.tab;
                    const form = document.querySelector(`.market-bet--buy-menu[data-tab="${tabData}"]`);
                    if (form) {
                        this.clearAllValidationErrors(form);
                        // Re-validate the form with new direction constraints
                        this.revalidateForm(form);
                    }
                }, 10);
            }
            
            // Handle sell market order button clicks to trigger corresponding bet item clicks
            const sellButton = e.target.closest('.sell_market_order_btn__js');
            if (sellButton) {
                // Extract team data from the parent container
                const sellContainer = sellButton.closest('.market-bet--current, .market-bet--current-2');
                if (sellContainer) {
                    const marketEventTeamId = sellContainer.getAttribute('data-market_event_team_id');
                    const team = sellContainer.getAttribute('data-team');
                    
                    if (marketEventTeamId && team) {
                        // Find the corresponding bet item in teams-match-info
                        const correspondingBetItem = document.querySelector(
                            `.teams-match-info .bet-item[data-market_event_team_id="${marketEventTeamId}"][data-team="${team}"]`
                        );
                        
                        if (correspondingBetItem) {
                            // Trigger click on the corresponding bet item
                            correspondingBetItem.click();
                            
                            // Also activate the sell tab in the Buy/sell market bet section
                            const sellTab = document.querySelector('a[href="#sell"].tabs__item.js-tab');
                            if (sellTab) {
                                sellTab.click();
                            }
                        }
                    }
                }
            }
        });
        
        
    }

    getMarketEventId() {
        
        // Try to get market event ID from various sources
        const marketEventElement = document.querySelector('[data-market_event_id]');
        if (marketEventElement) {
            const id = marketEventElement.getAttribute('data-market_event_id');
            return id;
        } else {
        }
        
        // Try to extract from URL path (/prediction/1/)
        const pathMatch = window.location.pathname.match(/\/prediction\/(\d+)\/?/);
        if (pathMatch) {
            return pathMatch[1];
        } else {
        }
        
        // Fallback to URL query params
        const urlParams = new URLSearchParams(window.location.search);
        const queryId = urlParams.get('market_event_id');
        if (queryId) {
            return queryId;
        } else {
        }
        
        return null;
    }

    // Набор команд, отрисованных сервером. На живой странице не меняется,
    // поэтому снимается один раз. Используется гейтом полноты ответа ордербука.
    collectExpectedTeamIds() {
        const ids = new Set();

        document.querySelectorAll('.bet-item[data-market_event_team_id]').forEach(item => {
            const id = item.getAttribute('data-market_event_team_id');
            if (id) ids.add(String(id));
        });

        return ids;
    }

    // Логика живёт чистой функцией в marketQuotes.js — иначе её не покрыть
    // юнит-тестом, не подняв весь этот модуль целиком.
    isCompleteOrderBookPayload(data) {
        return isCompleteOrderBookPayload(data, this.expectedTeamIds);
    }

    refreshMarketOrderForms(quotes) {
        const forms = [
            document.querySelector('.market-bet--buy-menu[data-tab="#buy"]'),
            document.querySelector('.market-bet--buy-menu[data-tab="#sell"]')
        ].filter(Boolean);

        if (!forms.length) return;

        const teamQuotes = quotes && this.currentMarketEventTeamId
            ? quotes.get(String(this.currentMarketEventTeamId))
            : null;
        const quote = teamQuotes && this.currentSelectedTeam
            ? teamQuotes[this.currentSelectedTeam]
            : null;

        forms.forEach(form => {
            const checkbox = form.querySelector('.js_cof_change_agree');

            // Лимитка не трогается ничем: ни цена, ни сумма, ни выводы.
            if (!checkbox || !checkbox.checked) return;

            const priceInput = form.querySelector('.js_price_per_share');

            if (priceInput && quote && !priceInput.hasAttribute('data-user-edited')) {
                const percent = parseFloat(quote.percent);
                if (Number.isFinite(percent)) {
                    const price = (percent / 100).toFixed(2);
                    priceInput.value = price;
                    priceInput.placeholder = price;
                }
            }

            // Существующий путь пересчёта: через simulateMarketBuy/Sell он уже
            // читает свежий this.orderBookData. Поля сумм и слайдеры не трогаем,
            // updateSellFormPositionData() не зовём — он сбрасывает qty.
            const triggerElement = form.querySelector('.input-range-value') || priceInput;
            if (triggerElement) {
                this.updateBetCalculations(triggerElement);
            }
        });
    }

    loadUserOrdersData() {
        
        try {
            const element = document.querySelector('.user_orders_data__js');
            if (!element) {
                return;
            }
            
            const jsonData = element.getAttribute('data-user_orders_data_json');
            if (!jsonData || jsonData.trim() === '') {
                return;
            }
            
            const orders = JSON.parse(jsonData);
            if (!Array.isArray(orders)) {
                console.warn('PredictionsPage: User orders data is not an array:', typeof orders);
                return;
            }
            
            
            // Filter out invalid orders and market orders, but keep all valid orders for dynamic filtering
            this.userOrdersData = orders.filter(order => {
                if (!order || !order.market_event_team_id) {
                    console.warn('PredictionsPage: Invalid order data:', order);
                    return false;
                }
                
                // Exclude market orders as they execute immediately and shouldn't appear in orderbook
                if (order.type === 'market') {
                    return false;
                }
                
                // Exclude orders with no value and no quantity
                const hasValue = (parseFloat(order.current_value) || 0) > 0;
                const hasQty = (parseFloat(order.current_qty) || 0) > 0;
                if (!hasValue && !hasQty) {
                    return false;
                }
                
                // Keep all valid orders for dynamic filtering by team/direction
                return true;
            });
            
            
        } catch (error) {
            console.error('PredictionsPage: Error loading user orders data:', error);
            this.userOrdersData = [];
        }
    }

    loadUserActiveBetsData() {
        
        try {
            const element = document.querySelector('.user_active_bets_data__js');
            if (!element) {
                return;
            }
            
            const jsonData = element.getAttribute('data-user_active_bets_data_json');
            if (!jsonData || jsonData.trim() === '') {
                return;
            }
            
            const activeBets = JSON.parse(jsonData);
            if (!Array.isArray(activeBets)) {
                console.warn('PredictionsPage: User active bets data is not an array:', typeof activeBets);
                return;
            }
            
            
            // Filter and validate active bets data
            this.userActiveBetsData = activeBets.filter(bet => {
                if (!bet || !bet.market_event_team_id) {
                    console.warn('PredictionsPage: Invalid bet data:', bet);
                    return false;
                }
                
                // Ensure required fields exist
                const hasValidData = bet.team && 
                                   typeof bet.avg_price !== 'undefined' && 
                                   typeof bet.qty !== 'undefined' && 
                                   typeof bet.value !== 'undefined';
                
                if (!hasValidData) {
                    console.warn('PredictionsPage: Bet missing required fields:', bet);
                    return false;
                }
                
                
                
                return true;
            });
            
            
        } catch (error) {
            console.error('PredictionsPage: Error loading user active bets data:', error);
            this.userActiveBetsData = [];
        }
    }

    hasUserOrders() {
        return Array.isArray(this.userOrdersData) && this.userOrdersData.length > 0;
    }

    hasUserActiveBets() {
        return Array.isArray(this.userActiveBetsData) && this.userActiveBetsData.length > 0;
    }

    getUserPositionForTeam(marketEventTeamId, team) {
        if (!this.hasUserActiveBets()) {
            return null;
        }
        
        
        const matchingPositions = this.userActiveBetsData.filter(bet => {
            const matchesTeam = marketEventTeamId ? 
                bet.market_event_team_id && bet.market_event_team_id.toString() === marketEventTeamId.toString() :
                true;
            const matchesOutcome = team ? bet.team === team : true;
            
            return matchesTeam && matchesOutcome;
        });
        
        if (matchingPositions.length === 0) {
            return null;
        }
        
        // Aggregate multiple positions for the same team/outcome
        let totalQty = 0;
        let totalValue = 0;
        
        matchingPositions.forEach(position => {
            const qty = parseFloat(position.qty) || 0;
            const value = parseFloat(position.value) || 0;
            
            totalQty += qty;
            totalValue += value;
        });
        
        // Calculate weighted average price
        const avgPrice = totalQty > 0 ? totalValue / totalQty : 0;
        
        const aggregatedPosition = {
            marketEventTeamId: marketEventTeamId,
            team: team,
            totalQty: totalQty,
            totalValue: totalValue,
            avgPrice: avgPrice,
            positions: matchingPositions
        };
        
        return aggregatedPosition;
    }

    getUserOrdersForTeam(marketEventTeamId, team, direction) {
        if (!this.hasUserOrders()) {
            return [];
        }
        
        
        const filtered = this.userOrdersData.filter(order => {
            if (!order) return false;
            
            const matchesTeam = marketEventTeamId ? 
                order.market_event_team_id && order.market_event_team_id.toString() === marketEventTeamId.toString() :
                true;
            const matchesOutcome = team ? order.team === team : true;
            const matchesDirection = direction ? order.direction === direction : true;
            
            const passes = matchesTeam && matchesOutcome && matchesDirection;
            
            if (passes) {
            }
            
            return passes;
        });
        
        return filtered;
    }

    async fetchOrderBookData(shouldCenter = false) {
        if (!this.marketEventId) {
            console.error('PredictionsPage: No market event ID available for API call');
            throw new Error('No market event ID available');
        }

        const apiUrl = `/engine/function/market_bets/order_book.php?market_event_id=${this.marketEventId}`;

        try {
            const response = await fetch(apiUrl);

            if (!response.ok) {
                console.error('PredictionsPage: API request failed with status:', response.status);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Валидация ДО любых изменений состояния страницы. Без неё битый,
            // но успешный ответ обнулял бы стакан, а applyQuotes по правилу
            // пропуска оставил бы на кнопках старые цены — страница
            // разъехалась бы сама с собой. Частичный ответ ещё хуже:
            // transformApiResponse() при отсутствии выбранной команды
            // возвращает стакан ЧУЖОЙ команды под заголовком текущей.
            if (!this.isCompleteOrderBookPayload(data)) {
                console.warn('PredictionsPage: incomplete order book payload, tick skipped');
                return;
            }

            this.orderBookData = this.transformApiResponse(data);

            this.renderOrderBook(shouldCenter);

            const quotes = deriveQuotes(data);
            applyQuotes(quotes);
            this.refreshMarketOrderForms(quotes);
        } catch (error) {
            console.error('PredictionsPage: Error in fetchOrderBookData:', error);
            throw error;
        }
    }

    transformApiResponse(apiData) {
        // Store all market event team data for filtering
        const allTeamData = {};
        const lastTradeData = {};
        
        if (apiData.response && Array.isArray(apiData.response) && apiData.response.length > 0) {
            // Process each market event team from the response
            apiData.response.forEach(responseItem => {
                if (responseItem.market_event_team_id && responseItem.orders) {
                    const teamId = responseItem.market_event_team_id.toString();
                    allTeamData[teamId] = { yes: { buy: {}, sell: {} }, no: { buy: {}, sell: {} } };
                    const orders = responseItem.orders;
                    
                    // Store last complete order data if available and valid
                    if (responseItem.last_complete_order && 
                        typeof responseItem.last_complete_order === 'object' && 
                        !Array.isArray(responseItem.last_complete_order) &&
                        responseItem.last_complete_order.type &&
                        responseItem.last_complete_order.direction &&
                        responseItem.last_complete_order.team &&
                        responseItem.last_complete_order.price !== undefined) {
                        
                        lastTradeData[teamId] = {
                            type: responseItem.last_complete_order.type,
                            direction: responseItem.last_complete_order.direction,
                            team: responseItem.last_complete_order.team,
                            price: parseFloat(responseItem.last_complete_order.price) || 0
                        };
                    } else {
                    }
                    
                    // Transform 'yes' orders with enhanced error handling
                    if (orders.yes && typeof orders.yes === 'object') {
                        // Transform buy orders
                        if (Array.isArray(orders.yes.buy)) {
                            orders.yes.buy.forEach(order => {
                                if (order && typeof order.price !== 'undefined' && 
                                    typeof order.qty !== 'undefined' && typeof order.value !== 'undefined') {
                                    allTeamData[teamId].yes.buy[order.price] = {
                                        qty: parseFloat(order.qty) || 0,
                                        value: parseFloat(order.value) || 0
                                    };
                                }
                            });
                        }
                        
                        // Transform sell orders
                        if (Array.isArray(orders.yes.sell)) {
                            orders.yes.sell.forEach(order => {
                                if (order && typeof order.price !== 'undefined' && 
                                    typeof order.qty !== 'undefined' && typeof order.value !== 'undefined') {
                                    allTeamData[teamId].yes.sell[order.price] = {
                                        qty: parseFloat(order.qty) || 0,
                                        value: parseFloat(order.value) || 0
                                    };
                                }
                            });
                        }
                    }
                    
                    // Transform 'no' orders with enhanced error handling
                    if (orders.no && typeof orders.no === 'object') {
                        // Transform buy orders (may not exist in API response)
                        if (Array.isArray(orders.no.buy)) {
                            orders.no.buy.forEach(order => {
                                if (order && typeof order.price !== 'undefined' && 
                                    typeof order.qty !== 'undefined' && typeof order.value !== 'undefined') {
                                    allTeamData[teamId].no.buy[order.price] = {
                                        qty: parseFloat(order.qty) || 0,
                                        value: parseFloat(order.value) || 0
                                    };
                                }
                            });
                        }
                        
                        // Transform sell orders
                        if (Array.isArray(orders.no.sell)) {
                            orders.no.sell.forEach(order => {
                                if (order && typeof order.price !== 'undefined' && 
                                    typeof order.qty !== 'undefined' && typeof order.value !== 'undefined') {
                                    allTeamData[teamId].no.sell[order.price] = {
                                        qty: parseFloat(order.qty) || 0,
                                        value: parseFloat(order.value) || 0
                                    };
                                }
                            });
                        }
                    }
                }
            });
        }
        
        // Store all team data and last trade data for later filtering
        this.allOrderBookData = allTeamData;
        this.lastTradeData = lastTradeData;
        
        // Return data for current team or first available team
        if (this.currentMarketEventTeamId && allTeamData[this.currentMarketEventTeamId]) {
            return allTeamData[this.currentMarketEventTeamId];
        } else if (Object.keys(allTeamData).length > 0) {
            // Return first available team data
            const firstTeamId = Object.keys(allTeamData)[0];
            return allTeamData[firstTeamId];
        }
        
        // Fallback to empty structure
        return { yes: { buy: {}, sell: {} }, no: { buy: {}, sell: {} } };
    }

    startOrderBookRefresh() {
        // Clear any existing interval
        if (this.orderBookRefreshInterval) {
            clearInterval(this.orderBookRefreshInterval);
        }
        
        // Set up periodic refresh
        this.orderBookRefreshInterval = setInterval(() => {
            this.fetchOrderBookData().catch(error => {
                console.error('Error refreshing order book:', error);
            });
        }, PredictionsPage.ORDER_BOOK_REFRESH_INTERVAL);
    }

    stopOrderBookRefresh() {
        if (this.orderBookRefreshInterval) {
            clearInterval(this.orderBookRefreshInterval);
            this.orderBookRefreshInterval = null;
        }
    }

    handleBetItemClick(betItem) {
        // eventDocument.js already called betItemFunc, so we just handle predictions-specific logic
        
        const team = betItem.getAttribute('data-team');
        const teamTitle = betItem.getAttribute('data-team_title');
        const teamLogo = betItem.getAttribute('data-team_logo');
        const percent = betItem.getAttribute('data-percent');
        const marketEventTeamId = betItem.getAttribute('data-market_event_team_id');
        
        
        this.currentSelectedTeam = team;
        this.currentMarketEventTeamId = marketEventTeamId;
        
        // Update form fields with selected team data
        this.updateFormWithTeamData({
            team,
            teamTitle,
            teamLogo,
            percent,
            marketEventTeamId
        });
        
        // Update price per share based on selection
        this.updateDefaultPrice(team, percent);

        // Recalculate bet metrics for both forms after team/bet type change
        const buyForm = document.querySelector('.market-bet--buy-menu[data-tab="#buy"]');
        const sellForm = document.querySelector('.market-bet--buy-menu[data-tab="#sell"]');

        if (buyForm) {
            const triggerElement = buyForm.querySelector('.input-range-value') || buyForm.querySelector('.js_price_per_share');
            if (triggerElement) {
                this.updateBetCalculations(triggerElement);
            }
            this.validatePrice(buyForm.querySelector('.js_price_per_share'), { allowEmpty: true });
        }

        if (sellForm) {
            const triggerElement = sellForm.querySelector('.input-range-value') || sellForm.querySelector('.js_price_per_share');
            if (triggerElement) {
                this.updateBetCalculations(triggerElement);
            }
            this.validatePrice(sellForm.querySelector('.js_price_per_share'), { allowEmpty: true });
        }
    }




    updateBuyFormFields(form, team, teamTitle, teamLogo, percent, marketEventTeamId) {
        // Update all form fields for buy section

        // Update hidden fields
        const marketEventTeamIdInput = form.querySelector('input[name="market_event_team_id"]');
        if (marketEventTeamIdInput) {
            marketEventTeamIdInput.value = marketEventTeamId;
        }

        // Update team logo - hide container if no logo
        const teamLogoImg = form.querySelector('.js_team_logo');
        if (teamLogoImg) {
            if (teamLogo) {
                // Show and update logo
                teamLogoImg.src = teamLogo;
                teamLogoImg.alt = teamTitle;
                teamLogoImg.classList.add('placeholder');
                // Show logo container if it was hidden
                const logoContainer = teamLogoImg.closest('.games-card-img');
                if (logoContainer) {
                    logoContainer.style.display = '';
                }
            } else {
                // Hide logo container when no image is available
                const logoContainer = teamLogoImg.closest('.games-card-img');
                if (logoContainer) {
                    logoContainer.style.display = 'none';
                }
            }
        }

        // Update team title
        const teamTitleSpan = form.querySelector('.js_team_title');
        if (teamTitleSpan && teamTitle) {
            teamTitleSpan.textContent = teamTitle;
        }

        // Update radio buttons based on form direction
        const direction = form.querySelector('input[name="direction"]')?.value;
        const radioName = direction === 'buy' ? 'team_buy' : 'team_sell';
        const radioButtons = form.querySelectorAll(`input[name="${radioName}"]`);
        radioButtons.forEach(radio => {
            const shouldBeChecked = radio.value === team;
            radio.checked = shouldBeChecked;
        });
        
        // Update price
        const priceInput = form.querySelector('.js_price_per_share');
        
        if (priceInput && direction && percent) {
            let price;
            if (direction === 'buy') {
                price = (percent / 100).toFixed(2);
            } else {
                price = ((100 - percent) / 100).toFixed(2);
            }
            
            priceInput.value = price;
            priceInput.placeholder = price;
        }
    }



    updateFormWithTeamData({ team, teamTitle, teamLogo, percent, marketEventTeamId }) {
        // Sync all related elements across the page (includes form updates)
        this.syncAllRelatedElements({ team, teamTitle, teamLogo, percent, marketEventTeamId });
    }

    syncAllRelatedElements({ team, teamTitle, teamLogo, percent, marketEventTeamId }) {
        // Смена команды или исхода отменяет ручную правку цены: ниже
        // updateBothForms() подставит цену новой выбранной кнопки.
        document.querySelectorAll('.market-bet--buy-menu .js_price_per_share').forEach(input => {
            input.removeAttribute('data-user-edited');
        });

        // Update bet-item active states (exclude user's current positions and orders)
        const allBetItems = document.querySelectorAll('.bet-item');
        allBetItems.forEach(item => {
            // Skip bet items that are inside user's current positions or orders
            const parentCurrentBet = item.closest('.market-bet--current, .market-bet--current-2');
            const parentOrder = item.closest('.market-bet--order');
            if (parentCurrentBet || parentOrder) {
                return; // Skip user's existing positions/orders
            }
            
            const itemTeam = item.getAttribute('data-team');
            const itemMarketId = item.getAttribute('data-market_event_team_id');
            
            if (itemTeam === team && itemMarketId === marketEventTeamId) {
                item.classList.add('active');
            } else if (itemMarketId === marketEventTeamId) {
                item.classList.remove('active');
            }
        });

        // Update rate displays for the specific bet-item that was selected (exclude user positions)
        const specificBetItems = document.querySelectorAll(`.bet-item[data-market_event_team_id="${marketEventTeamId}"][data-team="${team}"]`);
        specificBetItems.forEach(specificBetItem => {
            // Skip bet items that are inside user's current positions or orders
            const parentCurrentBet = specificBetItem.closest('.market-bet--current, .market-bet--current-2');
            const parentOrder = specificBetItem.closest('.market-bet--order');
            if (parentCurrentBet || parentOrder) {
                return; // Skip user's existing positions/orders
            }
            
            // Update the data-percent attribute
            specificBetItem.setAttribute('data-percent', percent);
            
            // Update the rate display within this specific bet-item
            const rateElement = specificBetItem.querySelector('.js_team_rate');
            if (rateElement) {
                // Use appropriate price based on team type
                let displayValue;
                if (team === 'yes') {
                    displayValue = specificBetItem.getAttribute('data-best_price_asc') || percent;
                } else if (team === 'no') {
                    displayValue = specificBetItem.getAttribute('data-best_price_asc') || percent;
                } else {
                    displayValue = percent;
                }
                
                rateElement.textContent = displayValue;
                // Also update data-rate attribute if it exists - keep using percent
                if (rateElement.hasAttribute('data-rate')) {
                    rateElement.setAttribute('data-rate', percent);
                }
            }
        });
        
        // Also update any rate displays that specifically match this team and market event ID
        // (but exclude user's current positions and orders)
        const rateElementsWithId = document.querySelectorAll(`.js_team_rate[data-market_event_team_id="${marketEventTeamId}"]`);
        if (rateElementsWithId.length > 0) {
            rateElementsWithId.forEach(element => {
                // Skip rate elements that are inside user's current positions or orders
                const parentCurrentBet = element.closest('.market-bet--current, .market-bet--current-2');
                const parentOrder = element.closest('.market-bet--order');
                if (parentCurrentBet || parentOrder) {
                    return; // Skip user's existing positions/orders
                }
                
                // Only update if this element is inside the correct bet-item or if it matches the team
                const parentBetItem = element.closest('.bet-item');
                if (!parentBetItem || parentBetItem.getAttribute('data-team') === team) {
                    // Use appropriate price based on team type
                    let displayValue;
                    if (parentBetItem) {
                        const betItemTeam = parentBetItem.getAttribute('data-team');
                        if (betItemTeam === 'yes') {
                            displayValue = parentBetItem.getAttribute('data-best_price_asc') || percent;
                        } else if (betItemTeam === 'no') {
                            displayValue = parentBetItem.getAttribute('data-best_price_asc') || percent;
                        } else {
                            displayValue = percent;
                        }
                    } else {
                        displayValue = percent;
                    }
                    
                    element.textContent = displayValue;
                    if (element.hasAttribute('data-rate')) {
                        element.setAttribute('data-rate', percent);
                    }
                }
            });
        }

        // Update block__item elements (both mobile and desktop)
        // but exclude user's current positions and orders
        const blockItems = document.querySelectorAll('.block__item:not(.market-bet--current):not(.market-bet--current-2):not(.market-bet--order)');
        blockItems.forEach(block => {
            const blockBetItems = block.querySelectorAll('.bet-item');
            blockBetItems.forEach(item => {
                const itemTeam = item.getAttribute('data-team');
                const itemMarketId = item.getAttribute('data-market_event_team_id');
                
                if (itemTeam === team && itemMarketId === marketEventTeamId) {
                    item.classList.add('active');
                } else if (itemMarketId === marketEventTeamId) {
                    item.classList.remove('active');
                }
            });
        });

        // Ensure BOTH buy and sell forms are updated with new selection
        this.updateBothForms({ team, teamTitle, teamLogo, percent, marketEventTeamId });

        // Clear validation errors from both forms after team data updates
        const buyForm = document.querySelector('.market-bet--buy-menu[data-tab="#buy"]');
        const sellForm = document.querySelector('.market-bet--buy-menu[data-tab="#sell"]');
        
        if (buyForm) {
            this.clearAllValidationErrors(buyForm);
        }
        if (sellForm) {
            this.clearAllValidationErrors(sellForm);
        }

        // Update sell form position data after team selection
        if (sellForm) {
            this.updateSellFormPositionData(sellForm, team);
        }

        // Update order book for selected team
        this.updateOrderBookForTeam(marketEventTeamId);
        
        // Update order book title
        this.updateOrderBookTitle(team, teamTitle, teamLogo);
        
        // Update central line to reflect new team/outcome selection
        this.updateCentralLine();

        publishPredictionChartTeamSelection(
            document.getElementById('chartMarketEvent'),
            marketEventTeamId
        );
        
        // Order book updates completed
    }

    updateOrderBookForTeam(marketEventTeamId) {

        // Update order book data for the selected team
        if (this.allOrderBookData && marketEventTeamId && this.allOrderBookData[marketEventTeamId]) {
            this.orderBookData = this.allOrderBookData[marketEventTeamId];
        } else {
            // Set empty order book data to show "No orders" state
            this.orderBookData = { yes: { buy: {}, sell: {} }, no: { buy: {}, sell: {} } };
        }

        // Always render the order book and center it (team change)
        this.renderOrderBook(true);
    }


    updateBothForms({ team, teamTitle, teamLogo, percent, marketEventTeamId }) {
        // Get both buy and sell forms
        const buyForm = document.querySelector('.market-bet--buy-menu[data-tab="#buy"]');
        const sellForm = document.querySelector('.market-bet--buy-menu[data-tab="#sell"]');
        const forms = [buyForm, sellForm].filter(form => form); // Filter out null forms
        
        
        forms.forEach(form => {
            const formType = form.getAttribute('data-tab');

            // Update hidden market_event_team_id field
            const marketEventTeamIdInput = form.querySelector('input[name="market_event_team_id"]');
            if (marketEventTeamIdInput) {
                marketEventTeamIdInput.value = marketEventTeamId;
            }

            // Update team logo - hide container if no logo
            const teamLogoImg = form.querySelector('.js_team_logo');
            if (teamLogoImg) {
                if (teamLogo) {
                    // Show and update logo
                    teamLogoImg.src = teamLogo;
                    teamLogoImg.alt = teamTitle;
                    teamLogoImg.classList.add('placeholder');
                    // Show logo container if it was hidden
                    const logoContainer = teamLogoImg.closest('.games-card-img');
                    if (logoContainer) {
                        logoContainer.style.display = '';
                    }
                } else {
                    // Hide logo container when no image is available
                    const logoContainer = teamLogoImg.closest('.games-card-img');
                    if (logoContainer) {
                        logoContainer.style.display = 'none';
                    }
                }
            }

            // Update team title
            const teamTitleSpan = form.querySelector('.js_team_title');
            if (teamTitleSpan && teamTitle) {
                teamTitleSpan.textContent = teamTitle;
            }

            // Update radio button selection based on form direction
            const radioName = formType === '#buy' ? 'team_buy' : 'team_sell';
            const radioButtons = form.querySelectorAll(`input[name="${radioName}"]`);
            
            radioButtons.forEach(radio => {
                const shouldBeChecked = radio.value === team;
                radio.checked = shouldBeChecked;
            });
            
            // Update price based on team selection (percent already contains the correct value for selected team)
            const priceInput = form.querySelector('.js_price_per_share');

            if (priceInput && percent) {
                const price = (percent / 100).toFixed(2);
                priceInput.value = price;
                priceInput.placeholder = price;
            }
        });
    }

    updateOrderBookTitle(team, teamTitle, teamLogo) {
        // Update ONLY order book titles (exclude user's current positions and orders)
        const orderBookTitles = document.querySelectorAll('.market-bet--order-book .card-wut--title');

        orderBookTitles.forEach(titleElement => {
            // Skip titles that are inside user's current positions or orders (extra safety check)
            const parentCurrentBet = titleElement.closest('.market-bet--current, .market-bet--current-2');
            const parentOrder = titleElement.closest('.market-bet--order');
            if (parentCurrentBet || parentOrder) {
                return; // Skip user's existing positions/orders
            }

            // Update team logo - hide container if no logo
            const logoContainer = titleElement.querySelector('.games-card-img');
            const logoImg = logoContainer?.querySelector('img');

            if (logoContainer) {
                if (teamLogo && logoImg) {
                    // Show and update logo
                    logoContainer.style.display = '';
                    logoImg.src = teamLogo;
                    logoImg.alt = teamTitle;
                    logoImg.classList.add('placeholder');
                } else {
                    // Hide logo container when no image is available
                    logoContainer.style.display = 'none';
                }
            }

            // Update team name
            const teamNameSpan = titleElement.querySelector('.overflow-ellipsis');
            if (teamNameSpan && teamTitle) {
                teamNameSpan.textContent = teamTitle;
            }
            
            // Update yes/no indicator
            const yesIndicator = titleElement.querySelector('.market-bet--yes');
            const noIndicator = titleElement.querySelector('.market-bet--no');
            
            // Remove existing indicators
            if (yesIndicator) yesIndicator.remove();
            if (noIndicator) noIndicator.remove();
            
            // Add new indicator based on team selection
            const indicatorSpan = document.createElement('span');
            if (team === 'yes') {
                indicatorSpan.className = 'market-bet--yes inline';
                indicatorSpan.innerHTML = `
                    <svg width="13" height="10" viewBox="0 0 13 10" class="icon icon-inline item-svg" data-img="/template/images/svg/yes-market-bet.svg">
                        <path id="yes-market-bet" style="fill-rule: evenodd;" d="M101.125,400a1.688,1.688,0,0,1,1.6,2.571,3.805,3.805,0,0,1-.532.545l-1.178,1.208-3.61,3.7c-0.632.648-1.331,1.713-2.318,1.948a1.547,1.547,0,0,1-1.368-.506l-3.458-3.584a1.807,1.807,0,0,1-.152-1.519,1.709,1.709,0,0,1,2.318-.857l1.786,1.792a2.391,2.391,0,0,0,.684.662,4.41,4.41,0,0,1,.608-0.623l1.406-1.442,2.394-2.454a8.9,8.9,0,0,1,1.14-1.168C100.64,400.135,100.92,400.141,101.125,400Z" transform="translate(-90 -400)"></path>
                    </svg> yes
                `;
            } else {
                indicatorSpan.className = 'market-bet--no inline';
                indicatorSpan.innerHTML = `
                    <svg width="10" height="10.031" viewBox="0 0 10 10.031" class="icon icon-inline item-svg" data-img="/template/images/svg/no-market-bet.svg">
                        <path id="no-market-bet" style="fill-rule: evenodd;" d="M107.156,400.014a2.179,2.179,0,0,1,1.872,1.037l1.3,1.271a2.491,2.491,0,0,0,.7.669,3.968,3.968,0,0,1,.569-0.568l1.3-1.305a4.958,4.958,0,0,1,1-.971,1.521,1.521,0,0,1,1.905.569,1.436,1.436,0,0,1-.334,1.774l-1.738,1.773a2.816,2.816,0,0,0-.736.769,4.026,4.026,0,0,1,.569.569l1.3,1.305a4.926,4.926,0,0,1,.969,1,1.523,1.523,0,0,1-.568,1.907,1.428,1.428,0,0,1-1.738-.3l-1.771-1.74a2.962,2.962,0,0,0-.8-0.77,3.924,3.924,0,0,1-.569.569l-1.3,1.305a4.926,4.926,0,0,1-1,.97,1.521,1.521,0,0,1-1.905-.568,1.435,1.435,0,0,1,.334-1.774l1.738-1.773a2.805,2.805,0,0,0,.736-0.77,4.019,4.019,0,0,1-.569-0.568l-1.3-1.305a4.926,4.926,0,0,1-.969-1,1.687,1.687,0,0,1-.067-1.171,1.79,1.79,0,0,1,.635-0.736C106.853,400.1,107.033,400.1,107.156,400.014Z" transform="translate(-106 -400)"></path>
                    </svg> no
                `;
            }
            
            titleElement.appendChild(indicatorSpan);
        });
    }

    updateDefaultPrice(team, percent) {
        const buyForm = document.querySelector('.market-bet--buy-menu[data-tab="#buy"]');
        const sellForm = document.querySelector('.market-bet--buy-menu[data-tab="#sell"]');

        // percent already contains the correct value for the selected team
        const price = (percent / 100).toFixed(2);

        if (buyForm) {
            const priceInput = buyForm.querySelector('.js_price_per_share');
            if (priceInput) {
                priceInput.value = price;
                priceInput.placeholder = price;
            }
        }

        if (sellForm) {
            const priceInput = sellForm.querySelector('.js_price_per_share');
            if (priceInput) {
                priceInput.value = price;
                priceInput.placeholder = price;
            }
        }
    }

    handleTeamSelection(radioButton) {
        const form = radioButton.closest('.market-bet--buy-menu');
        const team = radioButton.value;
        
        // Clear validation errors before team selection change
        this.clearAllValidationErrors(form);
        
        // Update current selection
        this.currentSelectedTeam = team;
        
        // Find the bet item for this team selection (exclude user positions)
        const betItems = document.querySelectorAll(`.bet-item[data-team="${team}"][data-market_event_team_id="${this.currentMarketEventTeamId}"]`);
        let betItem = null;

        for (const item of betItems) {
            // Skip bet items that are inside user's current positions or orders
            const parentCurrentBet = item.closest('.market-bet--current, .market-bet--current-2');
            const parentOrder = item.closest('.market-bet--order');
            if (!parentCurrentBet && !parentOrder) {
                betItem = item;
                break;
            }
        }
        
        if (betItem) {
            const teamTitle = betItem.getAttribute('data-team_title');
            const teamLogo = betItem.getAttribute('data-team_logo');
            const percent = betItem.getAttribute('data-percent');
            
            // Sync all related elements with new team selection (includes price updates)
            this.syncAllRelatedElements({ 
                team, 
                teamTitle, 
                teamLogo, 
                percent, 
                marketEventTeamId: this.currentMarketEventTeamId 
            });
        }
        
        // Update position-specific validation and calculations for both buy and sell forms
        const direction = form.querySelector('input[name="direction"]')?.value;
        if (direction === 'sell') {
            this.updateSellFormPositionData(form, team);
        } else if (direction === 'buy') {
            this.updateBuyFormConstraints(form, team);
        }
        
        // Re-validate the form with new team constraints
        this.revalidateForm(form);
    }
    
    updateSellFormPositionData(form, team) {
        
        const userPosition = this.getUserPositionForTeam(this.currentMarketEventTeamId, team);
        
        // Update quantity input constraints
        const qtyInput = form.querySelector('input[name="qty"]');
        const rangeSlider = form.querySelector('.input-range');
        
        if (userPosition && userPosition.totalQty > 0) {
            // User has position - update max values
            const maxQty = userPosition.totalQty;
            
            if (qtyInput) {
                qtyInput.setAttribute('max', maxQty);
                qtyInput.setAttribute('data-max_value_bet', maxQty);
                
                // Reset value if it exceeds new max
                if (parseFloat(qtyInput.value) > maxQty) {
                    qtyInput.value = Math.min(parseFloat(qtyInput.value), maxQty);
                }
            }
            
            if (rangeSlider) {
                rangeSlider.setAttribute('max', maxQty);
                if (parseFloat(rangeSlider.value) > maxQty) {
                    rangeSlider.value = Math.min(parseFloat(rangeSlider.value), maxQty);
                }
            }
            
            // Clear any previous error messages
            this.showValidationError(qtyInput, '', true);
            
        } else {
            // User has no position - show warning and reset values
            if (qtyInput) {
                qtyInput.setAttribute('max', 0);
                qtyInput.setAttribute('data-max_value_bet', 0);
                qtyInput.value = 0;
                
                this.showValidationError(qtyInput, translate("You don't own any {team} shares for this outcome", { team: translate(team.toUpperCase()) }), false);
            }
            
            if (rangeSlider) {
                rangeSlider.setAttribute('max', 0);
                rangeSlider.value = 0;
            }
        }
        
        // Update display immediately
        const price = parseFloat(form.querySelector('.js_price_per_share')?.value) || 0;
        const qty = parseFloat(qtyInput?.value) || 0;
        this.calculateSellMetrics(form, price, qty);
    }

    updateBuyFormConstraints(form) {
        // Update constraints for buy forms (user_betcoin input)
        const betcoinInput = form.querySelector('input[name="user_betcoin"]');
        const rangeSlider = form.querySelector('.input-range');
        
        if (betcoinInput) {
            // Get the max value from the HTML max attribute (110 betcoins)
            const maxBetcoin = parseFloat(betcoinInput.getAttribute('max')) || 110;
            
            // Set data-max_value_bet attribute for validation
            betcoinInput.setAttribute('data-max_value_bet', maxBetcoin);
            
            // Clear any previous error messages
            this.showValidationError(betcoinInput, '', true);
        }
        
        if (rangeSlider) {
            // Ensure range slider has the same max value
            const maxBetcoin = parseFloat(rangeSlider.getAttribute('max')) || 110;
            rangeSlider.setAttribute('max', maxBetcoin);
        }
    }

    handleOrderTypeChange(checkbox) {
        const form = checkbox.closest('.market-bet--buy-menu');
        const priceInput = form.querySelector('.js_price_per_share');
        const priceWrapper = priceInput.closest('.iconed-input');
        
        if (checkbox.checked) {
            // Market order - hide price input with animation
            this.animatePriceInputHide(priceWrapper, priceInput);
        } else {
            // Limit order - show price input with animation
            this.animatePriceInputShow(priceWrapper, priceInput);
        }
        
        // Маркет-ордер снимает ошибку цены, лимитка — проверяет её заново
        this.validatePrice(priceInput, { allowEmpty: true });

        // Recalculate all metrics when order type changes (for both buy and sell forms)
        this.updateBetCalculations(checkbox);
    }

    animatePriceInputHide(priceWrapper, priceInput) {
        // Use jQuery slideUp animation with callback for state changes
        $(priceWrapper).slideUp(300, () => {
            priceWrapper.classList.add('disabled');
            priceInput.disabled = true;
        });
    }

    animatePriceInputShow(priceWrapper, priceInput) {
        // Remove disabled state and enable input
        priceWrapper.classList.remove('disabled');
        priceInput.disabled = false;
        
        // Use jQuery slideDown animation
        $(priceWrapper).slideDown(300);
    }

    showAvgPriceRow(form, avgPrice, direction) {
        const displayList = form.querySelector('.block__item-list.full');
        if (!displayList) return;

        // Check if row already exists
        let avgPriceRow = displayList.querySelector('.avg-price-row');
        
        if (!avgPriceRow) {
            // Create new row
            avgPriceRow = document.createElement('li');
            avgPriceRow.className = 'avg-price-row';
            
            // Insert at the end of the list
            displayList.appendChild(avgPriceRow);
            
            // Hide initially for animation
            $(avgPriceRow).hide();
        }

        // Update content
        const avgPriceKey = direction === 'buy' ? 'Avg buy price:' : 'Avg sell price:';
        let content = `<span class="market_bet__shares_txt">${translate(avgPriceKey)}</span>`;
        content += `<b class="market_bet__shares_value"><span class="money-amount"><span>$</span><span class="">${avgPrice.toFixed(3)}</span></span></b>`;
        
        avgPriceRow.innerHTML = content;

        // Animate to show with jQuery
        $(avgPriceRow).slideDown(300);
    }

    hideAvgPriceRow(form) {
        const displayList = form.querySelector('.block__item-list.full');
        if (!displayList) return;

        const avgPriceRow = displayList.querySelector('.avg-price-row');
        if (!avgPriceRow) return;

        // Use jQuery slideUp animation with callback to remove element
        $(avgPriceRow).slideUp(300, () => {
            $(avgPriceRow).remove();
        });
    }

    updatePricePerShare(input) {
        const form = input.closest('.market-bet--buy-menu');
        
        // When price changes, re-validate the bet amount to show updated price context
        const betAmountInput = form.querySelector('input[name="user_betcoin"]');
        if (betAmountInput && parseFloat(betAmountInput.value) > 0) {
            this.validateBetAmount(betAmountInput);
        }
        
        this.updateBetCalculations(input);
    }

    updateBetCalculations(triggerElement) {
        const form = triggerElement.closest('.market-bet--buy-menu');
        if (!form) return;

        const direction = form.querySelector('input[name="direction"]').value;
        const priceInput = form.querySelector('.js_price_per_share');
        const betcoinInput = form.querySelector('input[name="user_betcoin"]');
        const qtyInput = form.querySelector('input[name="qty"]');
        
        const price = parseFloat(priceInput.value) || 0;
        const betcoin = parseFloat(betcoinInput?.value) || 0;
        const qty = parseFloat(qtyInput?.value) || 0;

        // Validate inputs before calculations (this will show/clear errors appropriately)
        if (direction === 'buy' && betcoinInput) {
            this.validateBetAmount(betcoinInput);
        } else if (direction === 'sell' && qtyInput) {
            this.validateBetAmount(qtyInput);
        }

        if (direction === 'buy') {
            this.calculateBuyMetrics(form, price, betcoin);
        } else {
            this.calculateSellMetrics(form, price, qty);
        }
    }

    calculateBuyMetrics(form, price, betcoin) {
        
        const currentTeam = this.currentSelectedTeam;
        const isMarketOrder = form.querySelector('.js_cof_change_agree')?.checked || false;
        
        let totalShares = 0;
        let avgBuyPrice = 0;
        let potentialWin = 0;
        let error = null;
        let filledValue = betcoin;
        
        // Handle zero input gracefully - show clean zero state
        if (betcoin === 0) {
            // For zero input, show clean zero values without errors
            totalShares = 0;
            avgBuyPrice = 0;
            potentialWin = 0;
            error = null;
            filledValue = 0;
        } else if (isMarketOrder) {
            // Use market simulation for market orders with non-zero input
            const simulation = this.simulateMarketBuy(betcoin, currentTeam);
            
            if (simulation.success) {
                totalShares = simulation.totalShares;
                avgBuyPrice = simulation.averagePrice;
                filledValue = simulation.filledValue;
            } else {
                error = simulation.error;
                totalShares = simulation.totalShares; // Partial fill shares
                avgBuyPrice = simulation.averagePrice;
                filledValue = simulation.filledValue;
            }
            
            // Calculate potential win for market orders
            potentialWin = totalShares > 0 ? totalShares * (1 - avgBuyPrice) : 0;
        } else {
            // Simple calculation for limit orders
            totalShares = price > 0 ? (betcoin / price) : 0;
            avgBuyPrice = price;
            potentialWin = totalShares * (1 - price);
        }
        
        this.updateBuyDisplay(form, {
            totalShares: totalShares,
            avgBuyPrice: avgBuyPrice,
            potentialWin: potentialWin,
            totalCost: filledValue,
            error: error,
            isMarketOrder: isMarketOrder,
            requestedValue: betcoin
        });
    }

    calculateSellMetrics(form, price, qty) {
        
        const currentTeam = this.currentSelectedTeam;
        const currentMarketEventTeamId = this.currentMarketEventTeamId;
        const isMarketOrder = form.querySelector('.js_cof_change_agree')?.checked || false;
        
        // Get user position for current team
        const userPosition = this.getUserPositionForTeam(currentMarketEventTeamId, currentTeam);
        
        if (!userPosition || userPosition.totalQty <= 0) {
            // No position available
            this.updateSellDisplay(form, {
                avgBuyPrice: 0,
                totalReceived: 0,
                profit: 0,
                profitPercent: 0,
                hasPosition: false,
                error: translate("You don't own any {team} shares for this outcome", { team: currentTeam ? translate(currentTeam.toUpperCase()) : '' })
            });
            return;
        }

        let totalReceived = 0;
        let error = null;
        let avgSellPrice = 0;
        let filledQty = qty; // Track actual filled quantity (defaults to requested for limit orders)

        if (isMarketOrder) {
            // Handle zero input gracefully - show clean zero state
            if (qty === 0) {
                totalReceived = 0;
                avgSellPrice = 0;
                error = null;
                filledQty = 0;
            } else {
                // Use market simulation for market orders
                const simulation = this.simulateMarketSell(qty);
                filledQty = simulation.filledQuantity || 0; // Capture actual filled quantity

                if (simulation.success) {
                    totalReceived = simulation.totalProceeds;
                    avgSellPrice = simulation.averagePrice;
                } else {
                    error = simulation.error;
                    totalReceived = simulation.totalProceeds; // Partial fill proceeds
                    avgSellPrice = simulation.averagePrice; // Still show avg price for partial fills
                }
            }
        } else {
            // Simple calculation for limit orders
            totalReceived = qty * price;
            // For limit orders, filledQty remains as requested qty (assumed full fill)
        }

        // Calculate profit/loss using actual filled quantity (not requested qty)
        // This ensures partial fills show correct profit based on shares actually sold
        const totalCost = filledQty * userPosition.avgPrice;
        const profit = totalReceived - totalCost;
        const profitPercent = totalCost > 0 ? (profit / totalCost) * 100 : 0;

        this.updateSellDisplay(form, {
            avgBuyPrice: userPosition.avgPrice,
            avgSellPrice: avgSellPrice,
            totalReceived: totalReceived,
            profit: profit,
            profitPercent: profitPercent,
            hasPosition: true,
            error: error,
            isMarketOrder: isMarketOrder,
            maxPosition: userPosition.totalQty,
            requestedQty: qty,
            filledQty: filledQty
        });
    }
    
    updateSellDisplay(form, metrics) {
        
        // Update average buy price display
        const avgPriceDisplay = form.querySelector('.market_bet__shares_value');
        if (avgPriceDisplay) {
            if (metrics.hasPosition) {
                avgPriceDisplay.innerHTML = `<span class="money-amount"><span>$</span><span class="">${metrics.avgBuyPrice.toFixed(2)}</span></span>`;
            } else {
                avgPriceDisplay.textContent = translate('No position');
            }
        }
        
        // Update profit display
        const profitDisplay = form.querySelector('.market_bet__shares_value').parentNode.nextElementSibling?.querySelector('.market_bet__shares_value');
        if (profitDisplay && metrics.hasPosition) {
            // Check if this is a zero input case
            const isZeroInput = metrics.requestedQty === 0;

            if (isZeroInput) {
                // Show clean $0.00 without percentage/arrow for zero input
                profitDisplay.className = 'market_bet__shares_value';
                profitDisplay.innerHTML = `<span class="money-amount"><span>$</span><span>0.00</span></span>`;
            } else {
                const profitClass = metrics.profit >= 0 ? 'c-green' : 'c-red';
                const profitSign = metrics.profit >= 0 ? '+' : '';
                const profitArrow = metrics.profit >= 0 ? '↑' : '↓';

                let profitText = `<span><strong>${profitSign}$</strong></span><strong><span>${Math.abs(metrics.profit).toFixed(2)}</span></strong> (${profitArrow}${Math.abs(metrics.profitPercent).toFixed(1)}%)`;

                profitDisplay.className = `market_bet__shares_value ${profitClass}`;
                profitDisplay.innerHTML = profitText;
            }
        }
        
        // Update "You'll get" display (clean, without average price)
        const rewardDisplay = form.querySelector('.market_bet__reward_value span:last-child');
        if (rewardDisplay) {
            if (metrics.error) {
                rewardDisplay.innerHTML = `<span class="c-orange">$${metrics.totalReceived.toFixed(2)} (${translate('partial')})</span>`;
            } else {
                rewardDisplay.innerHTML = `<span class="money-amount"><span>$</span><span class="">${metrics.totalReceived.toFixed(2)}</span></span>`;
            }
        }
        
        // Handle average price row for market orders
        if (metrics.isMarketOrder && metrics.avgSellPrice > 0) {
            this.showAvgPriceRow(form, metrics.avgSellPrice, 'sell');
        } else {
            this.hideAvgPriceRow(form);
        }
        
        // Show error messages if any
        if (metrics.error) {
            const qtyInput = form.querySelector('input[name="qty"]');
            if (qtyInput) {
                this.showValidationError(qtyInput, metrics.error, false);
            }
        } else {
            // Clear any existing errors when no error
            const qtyInput = form.querySelector('input[name="qty"]');
            if (qtyInput) {
                this.showValidationError(qtyInput, '', true);
            }
        }

        // Update hidden avg_price input for backend (market orders only)
        if (metrics.isMarketOrder && metrics.avgSellPrice > 0) {
            this.setHiddenFormInput(form, 'avg_price', metrics.avgSellPrice.toFixed(4));
        } else {
            // Remove avg_price for limit orders
            const avgPriceInput = form.querySelector('input[name="avg_price"]');
            if (avgPriceInput) avgPriceInput.remove();
        }
    }

    updateBuyDisplay(form, metrics) {
        
        // Update shares display (clean, without average price)
        const sharesDisplay = form.querySelector('.market_bet__shares_value');
        if (sharesDisplay) {
            // Check if this is a zero input case (no error should be shown for zero input)
            const isZeroInput = metrics.requestedValue === 0;
            
            if (metrics.error && metrics.totalShares === 0 && !isZeroInput) {
                sharesDisplay.innerHTML = `<span class="c-orange">0 (${translate('no liquidity')})</span>`;
            } else if (metrics.error && metrics.totalShares > 0) {
                sharesDisplay.innerHTML = `<span class="c-orange">${metrics.totalShares.toFixed(2)} (${translate('partial')})</span>`;
            } else {
                sharesDisplay.textContent = metrics.totalShares.toFixed(2);
            }
        }
        
        // Handle average price row for market orders
        if (metrics.isMarketOrder && metrics.avgBuyPrice > 0) {
            this.showAvgPriceRow(form, metrics.avgBuyPrice, 'buy');
        } else {
            this.hideAvgPriceRow(form);
        }
        
        // Update potential win display
        const rewardDisplay = form.querySelector('.market_bet__reward_value span:last-child');
        if (rewardDisplay) {
            // Check if this is a zero input case (no error should be shown for zero input)
            const isZeroInput = metrics.requestedValue === 0;
            
            if (metrics.error && metrics.potentialWin === 0 && !isZeroInput) {
                rewardDisplay.innerHTML = `<span class="c-orange">0 (${translate('no liquidity')})</span>`;
            } else if (metrics.error && metrics.potentialWin > 0) {
                rewardDisplay.innerHTML = `<span class="c-orange">$${metrics.potentialWin.toFixed(2)} (${translate('partial')})</span>`;
            } else {
                rewardDisplay.innerHTML = `<span class="money-amount"><span>$</span><span class="">${metrics.potentialWin.toFixed(2)}</span></span>`;
            }
        }
        
        // Update cost display if different from requested (for market orders with partial fills)
        const costDisplay = form.querySelector('.market_bet__cost_value');
        if (costDisplay && metrics.isMarketOrder && metrics.totalCost !== metrics.requestedValue) {
            costDisplay.innerHTML = `<span class="money-amount"><span>$</span><span class="">${metrics.totalCost.toFixed(2)}</span></span> <span class="fz-tiny c-orange">(of $${metrics.requestedValue.toFixed(2)})</span>`;
        }
        
        // Show error messages if any
        if (metrics.error) {
            const betcoinInput = form.querySelector('input[name="user_betcoin"]');
            if (betcoinInput) {
                this.showValidationError(betcoinInput, metrics.error, false);
            }
        } else {
            // Clear any existing errors when no error
            const betcoinInput = form.querySelector('input[name="user_betcoin"]');
            if (betcoinInput) {
                this.showValidationError(betcoinInput, '', true);
            }
        }

        // Update hidden avg_price input for backend (market orders only)
        if (metrics.isMarketOrder && metrics.avgBuyPrice > 0) {
            this.setHiddenFormInput(form, 'avg_price', metrics.avgBuyPrice.toFixed(4));
        } else {
            // Remove avg_price for limit orders
            const avgPriceInput = form.querySelector('input[name="avg_price"]');
            if (avgPriceInput) avgPriceInput.remove();
        }
    }

    handleOrderBookClick(orderBookLine) {
        const priceCell = orderBookLine.querySelector('.market-bet--order-book--cell:nth-child(2) b');
        if (!priceCell) return;
        
        // Extract price using regex to handle user order indicators (●$1.23)
        const priceMatch = priceCell.textContent.match(/\$(\d+\.?\d*)/);
        const price = priceMatch ? priceMatch[1] : null;
        
        // Validate extracted price
        if (!price || isNaN(parseFloat(price))) {
            console.warn('PredictionsPage: Could not extract valid price from order book line:', priceCell.textContent);
            return;
        }
        
        const activeTab = document.querySelector('.tabs__item.js-tab.active');
        
        if (activeTab) {
            const tabData = activeTab.getAttribute('href') || activeTab.dataset.tab;
            const form = document.querySelector(`.market-bet--buy-menu[data-tab="${tabData}"]`);
            
            if (form) {
                const priceInput = form.querySelector('.js_price_per_share');
                if (priceInput && !priceInput.disabled) {
                    priceInput.value = price;
                    // Клик по строке стакана — тоже осознанный выбор цены.
                    priceInput.setAttribute('data-user-edited', '1');
                    this.updateBetCalculations(priceInput);
                    this.validatePrice(priceInput, { allowEmpty: true });
                }
            }
        }
    }


    async initializeOrderBook() {

        // Try to fetch order book from API first
        if (this.marketEventId) {
            try {
                await this.fetchOrderBookData(true); // Center on first load
                return;
            } catch (e) {
                console.warn('PredictionsPage: Failed to fetch order book from API, falling back to DOM data:', e);
            } finally {
                // Поллинг обязан стартовать независимо от исхода первой загрузки.
                // Раньше startOrderBookRefresh() стоял после await в try: любой
                // сбой первичного запроса (сеть, 500) уводил в catch, и страница
                // навсегда оставалась на статике из DOM — живых обновлений не было
                // до перезагрузки. Единственное, что её оживляло, — переключение
                // вкладки: visibilitychange-обработчик зовёт startOrderBookRefresh().
                this.startOrderBookRefresh();
            }
        } else {
        }

        // Fallback to DOM-based data
        const orderBookElement = document.querySelector('[data-market_team_orders_json]');
        if (orderBookElement) {
            try {
                const orderBookJson = orderBookElement.getAttribute('data-market_team_orders_json');
                this.orderBookData = JSON.parse(orderBookJson);
                this.renderOrderBook(true); // Center on first load
            } catch (e) {
                console.error('PredictionsPage: Error parsing DOM order book data:', e);
            }
        } else {
        }
    }

    initializeChart() {
        // Initialize the market event chart
        const chartElement = document.querySelector('[data-chart="chartMarketEvent"]');
        if (chartElement) {
            this.chartOwner = Symbol('prediction-chart');
            this.chartModulePromise = charts(null, chartElement, this.chartOwner);
        }
    }

    scrollChildToCenter(container, child) {
        // Validate inputs
        if (!container || !child) {
            return;
        }

        // Check if container is scrollable
        if (container.scrollHeight <= container.clientHeight) {
            return;
        }

        const containerRect = container.getBoundingClientRect();
        const childRect = child.getBoundingClientRect();

        const offset =
            childRect.top -
            containerRect.top -
            container.clientHeight / 2 +
            childRect.height / 2;

        container.scrollTo({
            top: container.scrollTop + offset,
            behavior: 'smooth',
        });
    }

    renderOrderBook(shouldCenter = false) {
        const orderBookContainer = document.querySelector('#order_book_inner__js');

        if (!orderBookContainer) {
            console.error('PredictionsPage: Order book container #order_book_inner__js not found');
            return;
        }

        // Clear existing order book lines (except header and central)
        const existingLines = orderBookContainer.querySelectorAll('.market-bet--order-book--line:not(.market-bet--order-book--line-head):not(.market-bet--order-book--line-central)');
        existingLines.forEach(line => line.remove());

        const centralLine = orderBookContainer.querySelector('.market-bet--order-book--line-central');

        // Handle case where order book data is not available or empty
        if (!this.orderBookData || Object.keys(this.orderBookData).length === 0) {
            // Apply minimal styling for empty state - no excessive whitespace
            orderBookContainer.style.height = 'auto';
            orderBookContainer.style.minHeight = PredictionsPage.ORDER_BOOK_MIN_HEIGHT;
            orderBookContainer.style.overflowY = 'visible';
            orderBookContainer.style.overflowX = 'hidden';
            orderBookContainer.style.scrollBehavior = 'smooth';
            this.updateCentralLine(); // This will show "No orders" message
            return;
        }

        // Set static height for orderbook with scrolling (9 lines visible: 4 sell + 1 central + 4 buy)
        orderBookContainer.style.height = PredictionsPage.ORDER_BOOK_HEIGHT;
        orderBookContainer.style.overflowY = 'auto';
        orderBookContainer.style.overflowX = 'hidden';

        // Add smooth scrolling behavior
        orderBookContainer.style.scrollBehavior = 'smooth';

        // Get current orderbook context for filtering user orders
        const currentTeam = this.currentSelectedTeam || 'yes';
        const currentTeamId = this.currentMarketEventTeamId;

        // Build complete order book structure in memory first
        const completeOrderBook = this.buildCompleteOrderBook(currentTeam, currentTeamId);

        // Render the complete order book structure
        this.renderCompleteOrderBook(orderBookContainer, centralLine, completeOrderBook);

        // Update central line with current market data
        this.updateCentralLine();

        // Center the order book on the central line only when explicitly requested
        // (on initialization, team change, or yes/no toggle - not on data updates)
        if (shouldCenter) {
            // Use setTimeout to ensure DOM is fully rendered before scrolling
            setTimeout(() => {
                if (centralLine && !this.isDestroyed) {
                    this.scrollChildToCenter(orderBookContainer, centralLine);
                }
            }, PredictionsPage.ORDER_BOOK_SCROLL_DELAY);
        }

        // Order book rendering completed
    }


    parseOrderBookData(side) {
        const orders = [];
        const teamSelection = this.currentSelectedTeam || 'yes';
        const sideData = this.orderBookData[teamSelection];
        
        
        if (sideData && sideData[side]) {
            Object.entries(sideData[side]).forEach(([price, data]) => {
                if (data && typeof data.qty !== 'undefined' && typeof data.value !== 'undefined') {
                    orders.push({
                        price: parseFloat(price),
                        qty: parseFloat(data.qty),
                        value: parseFloat(data.value)
                    });
                }
            });
        } else {
        }

        // Sort orders by price descending (highest to lowest)
        const sortedOrders = orders.sort((a, b) => b.price - a.price);
            
        return sortedOrders;
    }

    simulateMarketSell(sellQuantity) {

        if (!this.orderBookData || !sellQuantity || sellQuantity <= 0) {
            return {
                success: false,
                error: translate('Invalid parameters for market sell'),
                totalProceeds: 0,
                averagePrice: 0,
                fills: []
            };
        }
        
        // Get buy orders (bids) for the selected team outcome
        const buyOrders = this.parseOrderBookData('buy');
        
        if (buyOrders.length === 0) {
            return {
                success: false,
                error: translate('No buyers available in order book'),
                totalProceeds: 0,
                averagePrice: 0,
                fills: []
            };
        }
        
        // Simulate order execution by walking down the order book
        let remainingQuantity = sellQuantity;
        let totalProceeds = 0;
        const fills = [];

        for (const order of buyOrders) {
            if (remainingQuantity <= 0) break;
            
            const availableQty = order.qty;
            const fillQty = Math.min(remainingQuantity, availableQty);
            const fillValue = fillQty * order.price;
            
            fills.push({
                price: order.price,
                quantity: fillQty,
                value: fillValue
            });
            
            totalProceeds += fillValue;
            remainingQuantity -= fillQty;
        }
        
        const filledQuantity = sellQuantity - remainingQuantity;
        const averagePrice = filledQuantity > 0 ? totalProceeds / filledQuantity : 0;
        
        const result = {
            success: remainingQuantity === 0,
            error: remainingQuantity > 0 ? translate('Only {filledQty} of {totalQty} shares can be filled', { 
                filledQty: filledQuantity.toFixed(2), 
                totalQty: sellQuantity 
            }) : null,
            totalProceeds: totalProceeds,
            averagePrice: averagePrice,
            fills: fills,
            filledQuantity: filledQuantity,
            remainingQuantity: remainingQuantity
        };
        
        return result;
    }

    simulateMarketBuy(buyValue) {

        if (!this.orderBookData || !buyValue || buyValue <= 0) {
            return {
                success: false,
                error: translate('Invalid parameters for market buy'),
                totalShares: 0,
                averagePrice: 0,
                fills: []
            };
        }
        
        // Get sell orders (asks) for the selected team outcome
        const sellOrders = this.parseOrderBookData('sell');
        
        if (sellOrders.length === 0) {
            return {
                success: false,
                error: translate('No sellers available in order book'),
                totalShares: 0,
                averagePrice: 0,
                fills: []
            };
        }
        
        // Sort sell orders by price ascending (lowest to highest) for market buy
        const sortedSellOrders = sellOrders.sort((a, b) => a.price - b.price);
        
        // Simulate order execution by walking up the order book
        let remainingValue = buyValue;
        let totalShares = 0;
        const fills = [];

        for (const order of sortedSellOrders) {
            if (remainingValue <= 0) break;
            
            const availableShares = order.qty;
            const orderValue = availableShares * order.price;
            
            if (remainingValue >= orderValue) {
                // Buy all shares at this price level
                const fillShares = availableShares;
                const fillValue = orderValue;
                
                fills.push({
                    price: order.price,
                    shares: fillShares,
                    value: fillValue
                });
                
                totalShares += fillShares;
                remainingValue -= fillValue;
            } else {
                // Partial fill - buy as many shares as remaining value allows
                const fillShares = remainingValue / order.price;
                const fillValue = remainingValue;
                
                fills.push({
                    price: order.price,
                    shares: fillShares,
                    value: fillValue
                });
                
                totalShares += fillShares;
                remainingValue = 0;
            }
        }
        
        const filledValue = buyValue - remainingValue;
        const averagePrice = totalShares > 0 ? filledValue / totalShares : 0;
        
        const result = {
            success: remainingValue === 0,
            error: remainingValue > 0 ? translate('Only ${filledValue} of ${totalValue} can be filled', { 
                filledValue: filledValue.toFixed(2), 
                totalValue: buyValue.toFixed(2) 
            }) : null,
            totalShares: totalShares,
            averagePrice: averagePrice,
            fills: fills,
            filledValue: filledValue,
            remainingValue: remainingValue
        };
        
        return result;
    }

    createOrderBookLine(order, side, totalValue, matchingUserOrders = []) {
        const line = document.createElement('div');
        line.className = `market-bet--order-book--line market-bet--order-book--line-${side}`;
        line.style.cursor = 'pointer';

        // Check if this line contains user orders
        const hasUserOrders = Array.isArray(matchingUserOrders) && matchingUserOrders.length > 0;

        // Add special class for user orders
        if (hasUserOrders) {
            line.classList.add('market-bet--order-book--line-my');
        }

        // Ensure we have valid numbers with fallbacks
        const price = isNaN(order.price) ? 0 : order.price;
        const qty = isNaN(order.qty) ? 0 : order.qty;
        const value = isNaN(order.value) ? 0 : order.value;

        // Calculate percentage based on this order's value relative to total value
        const percent = totalValue > 0 ? Math.max(0, Math.min(100, (value / totalValue) * 100)).toFixed(0) : 0;

        line.innerHTML = `
            <div class="market-bet--order-book--cell market-bet--order-book--cell-size volume-bar-hover" style="--percent:${percent}%;"></div>
            <div class="market-bet--order-book--cell"><b>$${price.toFixed(2)}</b></div>
            <div class="market-bet--order-book--cell">${qty.toFixed(0)}</div>
            <div class="market-bet--order-book--cell">$${value.toFixed(2)}</div>
        `;

        return line;
    }

    createEmptyOrderBookLine(side) {
        const line = document.createElement('div');
        line.className = `market-bet--order-book--line market-bet--order-book--line-${side}`;
        line.setAttribute('data-order-type', 'empty');

        // Maintain cell structure for consistent layout
        line.innerHTML = `
            <div class="market-bet--order-book--cell"></div>
            <div class="market-bet--order-book--cell"></div>
            <div class="market-bet--order-book--cell"></div>
            <div class="market-bet--order-book--cell"></div>
        `;

        return line;
    }

    createSpacerLine() {
        const spacer = document.createElement('div');
        spacer.className = 'market-bet--order-book--line market-bet--order-book--spacer';
        spacer.style.height = '40px'; // Same height as regular order lines
        spacer.style.display = 'none'; // Completely hidden, no space taken
        spacer.style.pointerEvents = 'none'; // Not clickable
        spacer.setAttribute('data-order-type', 'spacer');
        
        // Create empty cells to maintain structure
        spacer.innerHTML = `
            <div class="market-bet--order-book--cell"></div>
            <div class="market-bet--order-book--cell"></div>
            <div class="market-bet--order-book--cell"></div>
            <div class="market-bet--order-book--cell"></div>
        `;
        
        return spacer;
    }

    buildCompleteOrderBook(currentTeam, currentTeamId) {
        // Parse market order book data
        const marketSellOrders = this.parseOrderBookData('sell');
        const marketBuyOrders = this.parseOrderBookData('buy');
        
        // Calculate total values for percentage calculation
        const totalSellValue = marketSellOrders.reduce((sum, order) => sum + order.value, 0);
        const totalBuyValue = marketBuyOrders.reduce((sum, order) => sum + order.value, 0);
        
        // Get user orders for current context
        const userOrders = this.hasUserOrders() ? this.getUserOrdersForTeam(currentTeamId, currentTeam) : [];
        
        // Build complete sell orders (market + user)
        const completeSellOrders = this.mergeOrdersAtPriceLevel(marketSellOrders, userOrders.filter(o => o.direction === 'sell'), totalSellValue, 'sell');
        
        // Build complete buy orders (market + user)
        const completeBuyOrders = this.mergeOrdersAtPriceLevel(marketBuyOrders, userOrders.filter(o => o.direction === 'buy'), totalBuyValue, 'buy');
        
        // Sort orders properly by price
        completeSellOrders.sort((a, b) => b.price - a.price); // Highest price first for sell orders
        completeBuyOrders.sort((a, b) => b.price - a.price); // Highest price first for buy orders
        
        return {
            sellOrders: completeSellOrders,
            buyOrders: completeBuyOrders,
            totalSellValue,
            totalBuyValue
        };
    }
    
    mergeOrdersAtPriceLevel(marketOrders, userOrders, totalValue, direction) {
        const ordersByPrice = new Map();
        
        // Add market orders
        marketOrders.forEach(order => {
            const priceKey = order.price.toFixed(2);
            ordersByPrice.set(priceKey, {
                price: order.price,
                qty: order.qty,
                value: order.value,
                totalValue: totalValue,
                direction: direction,
                userOrders: [],
                isMarketOrder: true
            });
        });
        
        // Add or merge user orders
        userOrders.forEach(userOrder => {
            if (!userOrder.price) return;
            
            const price = parseFloat(userOrder.price);
            const priceKey = price.toFixed(2);
            const value = parseFloat(userOrder.current_value) || 0;
            const explicitQty = parseFloat(userOrder.current_qty) || 0;
            const effectiveQty = explicitQty > 0 ? explicitQty : (price > 0 ? value / price : 0);
            
            if (ordersByPrice.has(priceKey)) {
                // Merge with existing market order
                const existing = ordersByPrice.get(priceKey);
                existing.userOrders.push(userOrder);
            } else {
                // Create new user-only order
                ordersByPrice.set(priceKey, {
                    price: price,
                    qty: effectiveQty,
                    value: value,
                    totalValue: Math.max(totalValue, 1),
                    direction: direction,
                    userOrders: [userOrder],
                    isMarketOrder: false
                });
            }
        });
        
        return Array.from(ordersByPrice.values());
    }
    
    renderCompleteOrderBook(orderBookContainer, centralLine, completeOrderBook) {
        const { sellOrders, buyOrders } = completeOrderBook;
        const MIN_ROWS = 4;

        // Add empty sell rows FIRST (furthest from center)
        const sellRowsNeeded = Math.max(0, MIN_ROWS - sellOrders.length);
        for (let i = 0; i < sellRowsNeeded; i++) {
            const emptyLine = this.createEmptyOrderBookLine('sell');
            orderBookContainer.insertBefore(emptyLine, centralLine);
        }

        // Render sell orders (above central line, closest to center)
        sellOrders.forEach(order => {
            const orderLine = this.createOrderBookLine(order, 'sell', order.totalValue, order.userOrders);
            orderLine.setAttribute('data-order-type', order.isMarketOrder ? 'market' : 'user-only');
            orderLine.setAttribute('data-price', order.price.toFixed(2));
            orderBookContainer.insertBefore(orderLine, centralLine);
        });

        // Render buy orders (below central line, closest to center)
        buyOrders.forEach(order => {
            const orderLine = this.createOrderBookLine(order, 'buy', order.totalValue, order.userOrders);
            orderLine.setAttribute('data-order-type', order.isMarketOrder ? 'market' : 'user-only');
            orderLine.setAttribute('data-price', order.price.toFixed(2));
            orderBookContainer.appendChild(orderLine);
        });

        // Add empty buy rows LAST (furthest from center)
        const buyRowsNeeded = Math.max(0, MIN_ROWS - buyOrders.length);
        for (let i = 0; i < buyRowsNeeded; i++) {
            const emptyLine = this.createEmptyOrderBookLine('buy');
            orderBookContainer.appendChild(emptyLine);
        }
    }

    updateCentralLine() {
        const centralLine = document.querySelector('#order-book--central');
        if (!centralLine) {
            return;
        }

        // Calculate last price and spread from order book data
        const buyOrders = this.parseOrderBookData('buy');
        const sellOrders = this.parseOrderBookData('sell');
        
        
        // Handle edge cases
        if (buyOrders.length === 0 && sellOrders.length === 0) {
            // Empty order book
            centralLine.innerHTML = `
                <div class="market-bet--order-book--cell market-bet--order-book--cell--last">${translate('No orders')}</div>
                <div class="market-bet--order-book--cell">-</div>
            `;
            return;
        }

        // Get best bid (highest buy) and best ask (lowest sell)
        const bestBid = buyOrders.length > 0 ? Math.max(...buyOrders.map(o => o.price)) : null;
        const bestAsk = sellOrders.length > 0 ? Math.min(...sellOrders.map(o => o.price)) : null;
        

        let spreadDisplay = '-';
        let lastPriceDisplay = '-';

        // Get current context: team ID, selected outcome (yes/no), and direction (buy/sell)
        const currentTeamId = this.currentMarketEventTeamId;
        const currentOutcome = this.currentSelectedTeam || 'yes'; // yes/no
        
        // Get current active tab to determine direction (buy/sell)
        const activeTab = document.querySelector('.tabs__item.js-tab.active');
        const currentDirection = activeTab ? (activeTab.getAttribute('href') === '#buy' || activeTab.getAttribute('data-tab') === '#buy' ? 'buy' : 'sell') : 'buy';
        
        // Get last trade data for current team
        const lastTrade = this.lastTradeData && currentTeamId ? this.lastTradeData[currentTeamId] : null;
        
        
        // Validate that we have complete and valid trade data
        const hasValidTradeData = lastTrade && 
                                  lastTrade.team && 
                                  lastTrade.direction && 
                                  typeof lastTrade.price === 'number' && 
                                  lastTrade.price > 0;
        
        if (hasValidTradeData) {
            // Calculate price based on current context vs last trade
            let calculatedPrice = lastTrade.price;
            
            if (lastTrade.team === currentOutcome && lastTrade.direction === currentDirection) {
                // Exact match: same outcome (yes/no) and same direction (buy/sell)
                calculatedPrice = lastTrade.price;
            } else if (lastTrade.team === currentOutcome && lastTrade.direction !== currentDirection) {
                // Same outcome but opposite direction - use complement
                calculatedPrice = 1 - lastTrade.price;
            } else if (lastTrade.team !== currentOutcome) {
                // Different outcome (yes vs no) - calculate based on prediction market logic
                if (lastTrade.direction === currentDirection) {
                    // Same direction but different outcome - use complement
                    calculatedPrice = 1 - lastTrade.price;
                } else {
                    // Different outcome and different direction - use original price
                    calculatedPrice = lastTrade.price;
                }
            }
            
            lastPriceDisplay = `$${calculatedPrice.toFixed(2)}`;
        } else {
            // No valid trade data available - check if we have order book data for current team
            
            // Verify we have order book data for the current team ID
            const hasTeamOrderBookData = this.allOrderBookData && 
                                        currentTeamId && 
                                        this.allOrderBookData[currentTeamId] &&
                                        Object.keys(this.allOrderBookData[currentTeamId]).length > 0;
            
            if (!hasTeamOrderBookData) {
                lastPriceDisplay = translate('No data');
            } else {
                // Check specific order book data for current outcome and direction
                const teamOrders = this.orderBookData[currentOutcome];
                
                if (teamOrders && teamOrders[currentDirection]) {
                    const orders = Object.entries(teamOrders[currentDirection]);
                    if (orders.length > 0) {
                        // Get best price for current direction from order book
                        const prices = orders.map(([price]) => parseFloat(price));
                        const bestPrice = currentDirection === 'buy' ? Math.max(...prices) : Math.min(...prices);
                        lastPriceDisplay = `$${bestPrice.toFixed(2)}`;
                    } else {
                        // No orders for this outcome/direction
                        lastPriceDisplay = translate('No data');
                    }
                } else {
                    // No order book data for this outcome/direction
                    lastPriceDisplay = translate('No data');
                }
            }
        }

        // Calculate spread from order book
        if (bestBid !== null && bestAsk !== null) {
            const spread = bestAsk - bestBid;
            spreadDisplay = `$${spread.toFixed(2)}`;
        } else if (bestBid !== null) {
            spreadDisplay = translate('No asks');
        } else if (bestAsk !== null) {
            spreadDisplay = translate('No bids');
        }


        centralLine.innerHTML = `
            <div class="market-bet--order-book--cell market-bet--order-book--cell--last">${translate('Last:')}<b>${lastPriceDisplay}</b></div>
            <div class="market-bet--order-book--cell">${translate('Spread:')}<b>${spreadDisplay}</b></div>
        `;
    }



    validateForm(form) {
        let isValid = true;
        
        // Validate bet amount
        const amountInput = form.querySelector('.input-range-value');
        if (amountInput) {
            isValid = this.validateBetAmount(amountInput) && isValid;
        }
        
        // Validate price for limit orders
        const priceInput = form.querySelector('.js_price_per_share');

        if (priceInput) {
            isValid = this.validatePrice(priceInput) && isValid;
        }

        return isValid;
    }

    // Цена лимитки: 0.01–0.99. allowEmpty — пока юзер печатает, пустое поле
    // ошибкой не считается; на сабмите считается.
    validatePrice(priceInput, { allowEmpty = false } = {}) {
        if (!priceInput) return true;

        const form = priceInput.closest('.market-bet--buy-menu');
        const marketOrderCheckbox = form ? form.querySelector('.js_cof_change_agree') : null;

        // Маркет-ордер исполняется по стакану, поле цены в нём не участвует
        if (marketOrderCheckbox && marketOrderCheckbox.checked) {
            this.showValidationError(priceInput, '', true);
            return true;
        }

        const rawValue = (priceInput.value || '').trim();

        if (rawValue === '' && allowEmpty) {
            this.showValidationError(priceInput, '', true);
            return true;
        }

        const price = parseFloat(rawValue);
        const isValid = Number.isFinite(price)
            && price >= PredictionsPage.MIN_SHARE_PRICE
            && price <= PredictionsPage.MAX_SHARE_PRICE;

        this.showValidationError(
            priceInput,
            isValid ? '' : translate('Price must be between $0.01 and $0.99'),
            isValid
        );

        return isValid;
    }




    // Enhanced validation for bet amounts
    validateBetAmount(input) {
        const value = parseFloat(input.value) || 0;
        const form = input.closest('.market-bet--buy-menu');
        const direction = form ? form.querySelector('input[name="direction"]')?.value : null;
        
        let maxValue = parseFloat(input.getAttribute('data-max_value_bet')) || 0;
        const minValue = parseFloat(input.getAttribute('min')) || 0;
        
        let errorMessage = '';
        let isValid = true;

        // For sell forms, validate against user's actual position
        if (direction === 'sell' && input.name === 'qty') {
            const currentTeam = this.currentSelectedTeam;
            const currentMarketEventTeamId = this.currentMarketEventTeamId;
            
            if (currentTeam && currentMarketEventTeamId) {
                const userPosition = this.getUserPositionForTeam(currentMarketEventTeamId, currentTeam);
                
                if (!userPosition || userPosition.totalQty <= 0) {
                    errorMessage = translate("You don't own any {team} shares for this outcome", { team: translate(currentTeam.toUpperCase()) });
                    isValid = false;
                } else {
                    // Update max value to user's actual position
                    maxValue = userPosition.totalQty;
                    input.setAttribute('max', maxValue);
                    
                    // Update range slider if it exists
                    const rangeSlider = form.querySelector('.input-range');
                    if (rangeSlider) {
                        rangeSlider.setAttribute('max', maxValue);
                    }
                    
                    if (value > maxValue) {
                        errorMessage = translate('You can only sell up to {maxQty} shares (your current position)', { maxQty: maxValue.toFixed(2) });
                        isValid = false;
                    }
                }
            }
        }
        
        // Enhanced validation for buy orders with market order liquidity check
        if (isValid && direction === 'buy' && input.name === 'user_betcoin') {
            const isMarketOrder = form.querySelector('.js_cof_change_agree')?.checked || false;
            
            if (isMarketOrder && value > 0) {
                // Ensure market orders also respect the 110 betcoin cap
                if (value > 110) {
                    errorMessage = translate('Maximum bet amount is {maxValue} betcoins (market order)', { maxValue: 110 });
                    isValid = false;
                } else {
                    // Check market liquidity for market buy orders
                    const currentTeam = this.currentSelectedTeam;
                    const simulation = this.simulateMarketBuy(value, currentTeam);
                    
                    if (!simulation.success && simulation.filledValue === 0) {
                        errorMessage = simulation.error || translate('No sellers available in order book');
                        isValid = false;
                    } else if (!simulation.success && simulation.filledValue > 0) {
                        // Partial fill - show warning but don't prevent submission
                        errorMessage = translate('Warning: {error}', { error: simulation.error });
                        // Note: We're not setting isValid = false here, just showing a warning
                    }
                }
            }
        }
        
        // Standard validation for other cases
        if (isValid) {
            if (value < minValue) {
                errorMessage = translate('Minimum amount is {minValue}', { minValue });
                isValid = false;
            } else if (value > maxValue) {
                if (direction === 'sell') {
                    errorMessage = translate('Maximum sellable amount is {maxValue}', { maxValue: maxValue.toFixed(2) });
                } else {
                    // Enhanced error message for buy orders with price context
                    const priceInput = form.querySelector('.js_price_per_share');
                    const price = parseFloat(priceInput?.value) || 0;
                    
                    if (price > 0) {
                        const maxShares = (maxValue / price).toFixed(0);
                        errorMessage = translate('Maximum bet amount is {maxValue} betcoins (≈{maxShares} shares at ${price}/share)', { 
                            maxValue, 
                            maxShares, 
                            price: price.toFixed(2) 
                        });
                    } else {
                        errorMessage = translate('Maximum bet amount is {maxValue} betcoins', { maxValue });
                    }
                }
                isValid = false;
            }
        }

        this.showValidationError(input, errorMessage, isValid);
        return isValid;
    }

    getValidationErrorKey(input) {
        const rawKey = input.name || (input.className || '').split(' ')[0] || 'input';
        return rawKey.replace(/[^\w-]/g, '_');
    }

    showValidationError(input, message, isValid) {
        if (!input) return;

        const key = this.getValidationErrorKey(input);
        const form = input.closest('.market-bet--buy-menu');
        const scope = form || document;

        // Errors are keyed by input, so different fields never overwrite each other
        let errorElement = scope.querySelector(`.validation-error[data-error-for="${key}"]`);

        if (!isValid && message) {
            if (!errorElement) {
                errorElement = document.createElement('div');
                errorElement.className = 'validation-error';
                errorElement.dataset.errorFor = key;
                errorElement.style.cssText = PredictionsPage.VALIDATION_ERROR_STYLE;

                // Сообщение — блок во всю ширину формы, поэтому его место
                // после строки с полем. Внутри .iconed-input — флекс-строки
                // фиксированной высоты — оно налезало на соседний текст.
                const anchor = this.getValidationErrorAnchor(input, form);

                if (anchor && anchor.parentNode) {
                    anchor.parentNode.insertBefore(errorElement, anchor.nextSibling);
                } else {
                    input.parentNode.appendChild(errorElement);
                }
            }
            errorElement.textContent = message;
            this.setInputErrorState(input, true);
        } else {
            // Clear error state
            if (errorElement) {
                errorElement.remove();
            }
            this.setInputErrorState(input, false);
        }
    }

    // Строка формы, после которой встаёт сообщение: .bet__range либо
    // ближайший предок инпута, лежащий прямо в форме.
    getValidationErrorAnchor(input, form) {
        const betRangeContainer = input.closest('.bet__range');
        if (betRangeContainer) return betRangeContainer;
        if (!form) return input.parentNode;

        let node = input;
        while (node.parentNode && node.parentNode !== form) {
            node = node.parentNode;
        }

        return node.parentNode === form ? node : input.parentNode;
    }

    // Рамку поля цены рисует обёртка .iconed-input: красим её, иначе выходит
    // вторая скруглённая рамка внутри первой.
    setInputErrorState(input, hasError) {
        const wrapper = input.closest('.iconed-input');

        if (wrapper) {
            wrapper.style.boxShadow = hasError ? PredictionsPage.VALIDATION_INPUT_SHADOW : '';
            input.style.borderColor = '';
        } else {
            input.style.borderColor = hasError ? '#ff4444' : '';
        }
    }

    clearAllValidationErrors(form) {
        if (!form) return;
        
        // Remove all validation error elements
        const errorElements = form.querySelectorAll('.validation-error');
        errorElements.forEach(element => element.remove());
        
        // Reset border colors for all inputs
        const inputs = form.querySelectorAll('input, select, textarea');
        inputs.forEach(input => {
            input.style.borderColor = '';
        });

        // И подсветку обёрток полей
        form.querySelectorAll('.iconed-input').forEach(wrapper => {
            wrapper.style.boxShadow = '';
        });
    }

    revalidateForm(form) {
        if (!form) return;
        
        // Clear all existing validation errors first
        this.clearAllValidationErrors(form);
        
        // Re-validate bet amount input
        const amountInput = form.querySelector('.input-range-value');
        if (amountInput) {
            this.validateBetAmount(amountInput);
        }
        
        // Re-validate price input if it's a limit order
        const priceInput = form.querySelector('.js_price_per_share');

        if (priceInput) {
            this.validatePrice(priceInput, { allowEmpty: true });
        }
        
        // Trigger bet calculations which will show any calculation-related errors
        const triggerElement = amountInput || priceInput;
        if (triggerElement) {
            this.updateBetCalculations(triggerElement);
        }
    }

    // Show meaningful error messages for form submission
    handleFormError(error) {
        let message = translate('An error occurred while placing your bet.');
        
        if (error.response) {
            // Server responded with error status
            switch (error.response.status) {
                case 400:
                    message = translate('Invalid bet parameters. Please check your input.');
                    break;
                case 401:
                    message = translate('Please log in to place bets.');
                    break;
                case 403:
                    message = translate('You are not allowed to place this bet.');
                    break;
                case 429:
                    message = translate('Too many requests. Please wait before placing another bet.');
                    break;
                case 500:
                    message = translate('Server error. Please try again later.');
                    break;
            }
        } else if (error.message) {
            message = error.message;
        }

        displayMessage(message, 'error');
    }


    // Helper method to set hidden form input (creates if doesn't exist)
    setHiddenFormInput(form, name, value) {
        if (!form) return;

        let input = form.querySelector(`input[name="${name}"]`);
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.appendChild(input);
        }
        input.value = value;
    }

    // Progressive loading states
    setLoadingState(form, isLoading) {
        const button = form.querySelector('button[type="submit"]');
        const inputs = form.querySelectorAll('input, select, textarea');

        if (isLoading) {
            button?.classList.add('animation-loading');
            inputs.forEach(input => input.disabled = true);
        } else {
            button?.classList.remove('animation-loading');
            inputs.forEach(input => input.disabled = false);
        }
    }
}

// Initialize predictions page when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
        const predictionsPage = new PredictionsPage();
        
        // Clean up on page unload
        window.addEventListener('beforeunload', () => {
            if (predictionsPage && !predictionsPage.isDestroyed) {
                predictionsPage.destroy();
            }
        });
});

export default PredictionsPage;
